import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, ChevronDown, Heart, LogOut, Sparkles, Package, Search, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { HeaderLogo } from './Logo';
import SearchBox from './SearchBox';

// Custom Link that scrolls to top
const ScrollToTopLink = ({ to, children, className, onClick }) => {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'instant' });
    navigate(to);
    if (onClick) onClick();
  };
  
  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

const Header = () => {
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const { getCartCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { wishlistCount } = useWishlist();
  const { currency, setCurrency, currencies } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Dynamic spacer sizing, the header is `position: fixed` with variable
  // height (logo image, optional tagline, nav row), so a hard-coded spacer
  // can't track it across viewports / locales / scroll states. We measure
  // the rendered header and mirror its height onto the spacer so content
  // below never gets clipped (e.g. the "Licensed Professionals" banner on
  // product pages was being half-hidden when the spacer was undersized).
  const headerEl = useRef(null);
  const [headerH, setHeaderH] = useState(140);
  const langDropdownRef = useRef(null);
  const currencyDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'EN', flag: '🇬🇧' },
    { code: 'es', name: 'ES', flag: '🇪🇸' },
    { code: 'ru', name: 'RU', flag: '🇷🇺' },
    { code: 'de', name: 'DE', flag: '🇩🇪' },
    { code: 'fr', name: 'FR', flag: '🇫🇷' },
    { code: 'it', name: 'IT', flag: '🇮🇹' },
    { code: 'pl', name: 'PL', flag: '🇵🇱' },
    { code: 'nl', name: 'NL', flag: '🇳🇱' },
    { code: 'pt', name: 'PT', flag: '🇧🇷' }
  ];

  // Scroll detection for compact header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track the header's actual rendered height. Triggered on mount, on scroll
  // (compact mode shrinks the header), on viewport resize, and on locale
  // changes (different translated taglines wrap to different heights).
  useEffect(() => {
    if (!headerEl.current) return undefined;
    const measure = () => {
      if (headerEl.current) {
        setHeaderH(headerEl.current.getBoundingClientRect().height);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(headerEl.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isScrolled]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
        setCurrencyDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll while the mobile burger menu is open so users can scroll
  // the menu itself instead of the page behind it. Restores prior overflow on close.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    setUserDropdownOpen(false);
    navigate('/');
  };

  const currentLang = languages.find(l => l.code === currentLanguage) || languages[0];
  const cartCount = getCartCount();

  // Admin gate, same whitelist as AdminPage.js (single source of truth here as
  // a lightweight UI gate; the /admin route + every /api/admin/* endpoint
  // re-validates server-side so this is purely a "show the link" check).
  const ADMIN_EMAILS = ['info@penlabpeptide.com', 'info@penpeptido.com'];
  const isAdmin = isAuthenticated && user && ADMIN_EMAILS.includes(user.email);

  // 9-language labels for header chrome, user dropdown items, Admin link,
  // Subscribe & Save nav. Replaces prior EN/ES/RU ternaries that fell back
  // to English for the 6 newer locales.
  const HEADER_LBL = {
    en: { admin: 'Admin', subscribe: 'Subscribe & Save', myProfile: 'My Profile', wishlist: 'Wishlist', recommendations: 'Recommendations', myOrders: 'My Orders', logout: 'Logout', login: 'Login' },
    es: { admin: 'Admin', subscribe: 'Suscríbete', myProfile: 'Mi Perfil', wishlist: 'Favoritos', recommendations: 'Recomendaciones', myOrders: 'Mis Pedidos', logout: 'Cerrar Sesión', login: 'Iniciar Sesión' },
    ru: { admin: 'Админ', subscribe: 'Подписка', myProfile: 'Профиль', wishlist: 'Избранное', recommendations: 'Рекомендации', myOrders: 'Мои Заказы', logout: 'Выйти', login: 'Войти' },
    de: { admin: 'Admin', subscribe: 'Abo & Sparen', myProfile: 'Mein Profil', wishlist: 'Wunschliste', recommendations: 'Empfehlungen', myOrders: 'Meine Bestellungen', logout: 'Abmelden', login: 'Anmelden' },
    fr: { admin: 'Admin', subscribe: 'Abonnez-vous', myProfile: 'Mon Profil', wishlist: 'Favoris', recommendations: 'Recommandations', myOrders: 'Mes Commandes', logout: 'Déconnexion', login: 'Connexion' },
    it: { admin: 'Admin', subscribe: 'Abbonati', myProfile: 'Il Mio Profilo', wishlist: 'Preferiti', recommendations: 'Raccomandazioni', myOrders: 'I Miei Ordini', logout: 'Esci', login: 'Accedi' },
    pl: { admin: 'Admin', subscribe: 'Subskrybuj', myProfile: 'Mój Profil', wishlist: 'Ulubione', recommendations: 'Rekomendacje', myOrders: 'Moje Zamówienia', logout: 'Wyloguj', login: 'Zaloguj' },
    nl: { admin: 'Admin', subscribe: 'Abonneren', myProfile: 'Mijn Profiel', wishlist: 'Verlanglijst', recommendations: 'Aanbevelingen', myOrders: 'Mijn Bestellingen', logout: 'Uitloggen', login: 'Inloggen' },
    pt: { admin: 'Admin', subscribe: 'Assine e Economize', myProfile: 'Meu Perfil', wishlist: 'Favoritos', recommendations: 'Recomendações', myOrders: 'Meus Pedidos', logout: 'Sair', login: 'Entrar' },
    au: { admin: 'Admin', subscribe: 'Subscribe & Save', myProfile: 'My Profile', wishlist: 'Wishlist', recommendations: 'Recommendations', myOrders: 'My Orders', logout: 'Logout', login: 'Login' },
  };
  const HL = HEADER_LBL[currentLanguage] || HEADER_LBL.en;
  const adminLabel = HL.admin;

  // Added "Home" as first nav item.
  // NOTE: /bulk is intentionally absent from the public nav per user
  // request (iter-61). The page is gated behind login + verification
  // for human users, but remains crawlable for SEO + AI-search citations
  // via the BulkPage component's bot-detection bypass.
  const navLinks = [
    { path: '/', label: t('nav_home') },
    { path: '/products', label: t('nav_products') },
    { path: '/subscriptions', label: HL.subscribe },
    { path: '/peptides', label: t('nav_about_peptides') },
    { path: '/blog', label: t('nav_blog') },
    { path: '/about', label: t('nav_about') },
    { path: '/technology', label: t('nav_technology') },
    { path: '/faqs', label: t('nav_faqs') },
    { path: '/contact', label: t('nav_contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header ref={headerEl} className="w-full fixed top-0 z-50 bg-white shadow-md">
        {/* Header - Logo always same size */}
        <div className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-center items-center">
              <HeaderLogo />
            </div>
            {/* Tagline - Hide when scrolled */}
            {!isScrolled && (
              <div className="text-center text-sm font-medium text-gray-700 pb-1">
                {t('header_title')}
              </div>
            )}
          </div>
        </div>

      {/* Main Navigation Bar - All links in one row */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex items-center h-12 gap-1 md:gap-2">
            {/* Left Section - Mobile menu */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Mobile Menu Button */}
              <div className="lg:hidden">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="p-2 rounded-2xl hover:bg-capsule-teal/10"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-btn"
                >
                  {mobileMenuOpen ? <X className="h-5 w-5 text-capsule-teal" /> : <Menu className="h-5 w-5 text-gray-600" />}
                </Button>
              </div>

              {/* Search Box - Desktop only (hidden on mobile/tablet) */}
              <div className="hidden lg:block w-48 xl:w-56">
                <SearchBox />
              </div>
            </div>

            {/* Center Section - ALL Navigation Links (Desktop), horizontally
                scrollable on narrow desktops so we never push the right cluster
                off-screen. Scrollbar hidden via .nav-scroll-x utility. */}
            <nav
              className="hidden lg:flex flex-1 min-w-0 items-center justify-center gap-1 xl:gap-2 overflow-x-auto nav-scroll-x"
              data-testid="header-nav-desktop"
            >
              {navLinks.map((link) => (
                <ScrollToTopLink
                  key={link.path}
                  to={link.path}
                  className={`px-2 xl:px-3 py-2 text-xs xl:text-sm font-medium rounded-xl transition-all whitespace-nowrap ${
                    isActive(link.path)
                      ? 'text-capsule-teal bg-capsule-teal/10'
                      : 'text-gray-600 hover:text-capsule-teal hover:bg-capsule-teal/5'
                  }`}
                >
                  {link.label}
                </ScrollToTopLink>
              ))}
            </nav>

            {/* Right Section - Language, Currency, User, Cart - ALWAYS at far
                right, never shrinks, never wraps. */}
            <div
              className="flex items-center gap-1 md:gap-2 ml-auto flex-shrink-0 flex-nowrap"
              data-testid="header-right-cluster"
            >
              {/* Language Selector */}
              <div className="relative" ref={langDropdownRef}>
                <button
                  onClick={() => { setLangDropdownOpen(!langDropdownOpen); setCurrencyDropdownOpen(false); }}
                  className="flex items-center gap-1 p-2 rounded-2xl hover:bg-capsule-teal/10 transition-all"
                  data-testid="language-dropdown-trigger"
                >
                  {/* Show the current-language flag directly on the trigger. */}
                  <span
                    className="text-lg md:text-xl leading-none"
                    aria-label={`Current language: ${currentLang.name}`}
                    data-testid="language-trigger-icon"
                  >
                    {currentLang.flag}
                  </span>
                  <ChevronDown className={`h-3 w-3 md:h-4 md:w-4 text-gray-500 transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {langDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 bg-white/95 backdrop-blur-xl border border-capsule-bg-dark/30 rounded-2xl shadow-soft-lg py-2 z-50 min-w-[60px] overflow-hidden">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setLangDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2 flex items-center justify-center hover:bg-capsule-teal/10 transition-colors ${
                          currentLanguage === lang.code ? 'bg-capsule-teal/15' : ''
                        }`}
                        data-testid={`language-option-${lang.code}`}
                      >
                        <span className="text-lg md:text-xl">{lang.flag}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Currency Selector, placed immediately next to the language selector */}
              <div className="relative" ref={currencyDropdownRef}>
                <button
                  onClick={() => { setCurrencyDropdownOpen(!currencyDropdownOpen); setLangDropdownOpen(false); }}
                  className="flex items-center gap-1 px-2 py-2 rounded-2xl hover:bg-capsule-teal/10 transition-all"
                  data-testid="currency-dropdown-trigger"
                  aria-label={`Current currency: ${currency}`}
                >
                  <span className="text-sm md:text-base font-semibold tabular-nums text-gray-700" data-testid="currency-trigger-symbol">
                    {(currencies.find(c => c.code === currency) || currencies[0]).symbol}
                  </span>
                  <ChevronDown className={`h-3 w-3 md:h-4 md:w-4 text-gray-500 transition-transform ${currencyDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {currencyDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 bg-white/95 backdrop-blur-xl border border-capsule-bg-dark/30 rounded-2xl shadow-soft-lg py-2 z-50 min-w-[140px] overflow-hidden">
                    {currencies.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => {
                          setCurrency(c.code);
                          setCurrencyDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-capsule-teal/10 transition-colors ${
                          currency === c.code ? 'bg-capsule-teal/15' : ''
                        }`}
                        data-testid={`currency-option-${c.code}`}
                      >
                        <span className="w-5 text-center font-semibold text-gray-800">{c.symbol}</span>
                        <span className="text-sm font-medium text-gray-700">{c.code}</span>
                        <span className="ml-auto text-[11px] text-gray-400">{c.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative hidden md:block" ref={userDropdownRef}>
                {isAuthenticated ? (
                  <>
                    <button
                      onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                      className="flex items-center gap-1 p-2 rounded-2xl hover:bg-capsule-teal/10 transition-all"
                      data-testid="user-dropdown-trigger"
                    >
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full ring-2 ring-capsule-teal/30" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center text-white text-xs font-bold">
                          {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <ChevronDown className={`h-3 w-3 text-gray-500 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {userDropdownOpen && (
                      <div className="absolute right-0 top-full mt-2 bg-white/95 backdrop-blur-xl border border-capsule-bg-dark/30 rounded-2xl shadow-soft-lg py-2 z-50 min-w-[200px] overflow-hidden">
                        <div className="px-4 py-3 border-b border-capsule-bg-dark/20">
                          <p className="font-semibold text-gray-900 text-sm truncate">{user?.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>
                        <ScrollToTopLink
                          to="/profile"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-capsule-teal/10 transition-colors"
                        >
                          <User className="w-4 h-4 text-capsule-teal" />
                          {HL.myProfile}
                        </ScrollToTopLink>
                        <ScrollToTopLink
                          to="/wishlist"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-capsule-teal/10 transition-colors"
                        >
                          <Heart className="w-4 h-4 text-capsule-coral" />
                          {HL.wishlist}
                          {wishlistCount > 0 && (
                            <span className="ml-auto bg-capsule-coral/20 text-capsule-coral text-xs px-2 py-0.5 rounded-full font-medium">{wishlistCount}</span>
                          )}
                        </ScrollToTopLink>
                        <ScrollToTopLink
                          to="/recommendations"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-capsule-teal/10 transition-colors"
                        >
                          <Sparkles className="w-4 h-4 text-capsule-aqua" />
                          {HL.recommendations}
                        </ScrollToTopLink>
                        <ScrollToTopLink
                          to="/orders"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-capsule-teal/10 transition-colors"
                        >
                          <Package className="w-4 h-4 text-capsule-mint" />
                          {HL.myOrders}
                        </ScrollToTopLink>
                        {isAdmin && (
                          <ScrollToTopLink
                            to="/admin"
                            onClick={() => setUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 text-sm text-capsule-teal hover:bg-capsule-teal/10 transition-colors font-semibold"
                            data-testid="user-menu-admin-link"
                          >
                            <ShieldCheck className="w-4 h-4 text-capsule-teal" />
                            {adminLabel}
                          </ScrollToTopLink>
                        )}
                        <div className="border-t border-capsule-bg-dark/20 mt-1 pt-1">
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-capsule-coral hover:bg-capsule-coral/10 transition-colors"
                          >
                            <LogOut className="w-4 h-4" />
                            {HL.logout}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <ScrollToTopLink to="/login">
                    <Button variant="ghost" size="sm" className="p-2 rounded-2xl hover:bg-capsule-teal/10">
                      <User className="h-5 w-5 text-gray-500" />
                    </Button>
                  </ScrollToTopLink>
                )}
              </div>
              
              {/* Wishlist Icon */}
              {isAuthenticated && (
                <ScrollToTopLink to="/wishlist" className="hidden lg:block">
                  <Button variant="ghost" size="sm" className="p-2 rounded-2xl relative hover:bg-capsule-coral/10">
                    <Heart className="h-5 w-5 text-gray-500" />
                    {wishlistCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-gradient-to-r from-capsule-coral to-capsule-salmon text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-coral-glow">
                        {wishlistCount}
                      </span>
                    )}
                  </Button>
                </ScrollToTopLink>
              )}
              
              {/* Cart */}
              <ScrollToTopLink to="/cart">
                <Button variant="ghost" size="sm" className="p-2 rounded-2xl relative hover:bg-capsule-teal/10" data-testid="header-cart-button">
                  <ShoppingCart className="h-5 w-5 text-gray-500" />
                  {cartCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-teal-glow">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </ScrollToTopLink>

              {/* Admin shortcut, only rendered when the logged-in user is in
                  the admin whitelist. Server-side admin gate still re-validates
                  every /api/admin/* call, this is just a discreet entry point. */}
              {isAdmin && (
                <ScrollToTopLink to="/admin">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2 rounded-2xl hover:bg-capsule-teal/10 hidden sm:inline-flex items-center gap-1"
                    data-testid="header-admin-link"
                    aria-label={adminLabel}
                    title={adminLabel}
                  >
                    <ShieldCheck className="h-5 w-5 text-capsule-teal" />
                    <span className="text-xs font-semibold text-capsule-teal hidden xl:inline">{adminLabel}</span>
                  </Button>
                </ScrollToTopLink>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu, fully scrollable when content exceeds viewport */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-capsule-bg-dark/30 shadow-soft-lg overflow-y-auto overscroll-contain"
          style={{ maxHeight: 'calc(100dvh - 64px)', WebkitOverflowScrolling: 'touch' }}
          data-testid="mobile-menu-container"
        >
          <div className="px-4 py-3">
            {/* Search Box in Mobile Menu */}
            <div className="mb-4">
              <SearchBox className="w-full" />
            </div>
            
            <nav className="py-2 space-y-1">
              {navLinks.map((link) => (
                <ScrollToTopLink 
                  key={link.path}
                  to={link.path} 
                  className={`block py-3 px-4 rounded-2xl transition-all ${
                    isActive(link.path) 
                      ? 'text-capsule-teal bg-capsule-teal/10 font-semibold' 
                      : 'text-gray-700 hover:bg-capsule-teal/5'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </ScrollToTopLink>
              ))}
            </nav>
            
            {/* User options in mobile menu */}
            {isAuthenticated ? (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-1">
                <ScrollToTopLink
                  to="/profile"
                  className="flex items-center gap-3 py-3 px-4 rounded-2xl text-gray-700 hover:bg-capsule-teal/5"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="w-4 h-4 text-capsule-teal" />
                  {HL.myProfile}
                </ScrollToTopLink>
                <ScrollToTopLink
                  to="/wishlist"
                  className="flex items-center gap-3 py-3 px-4 rounded-2xl text-gray-700 hover:bg-capsule-teal/5"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Heart className="w-4 h-4 text-capsule-coral" />
                  {HL.wishlist}
                  {wishlistCount > 0 && (
                    <span className="ml-auto bg-capsule-coral/20 text-capsule-coral text-xs px-2 py-0.5 rounded-full font-medium">{wishlistCount}</span>
                  )}
                </ScrollToTopLink>
                <ScrollToTopLink
                  to="/orders"
                  className="flex items-center gap-3 py-3 px-4 rounded-2xl text-gray-700 hover:bg-capsule-teal/5"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Package className="w-4 h-4 text-capsule-mint" />
                  {HL.myOrders}
                </ScrollToTopLink>
                {isAdmin && (
                  <ScrollToTopLink
                    to="/admin"
                    className="flex items-center gap-3 py-3 px-4 rounded-2xl text-capsule-teal hover:bg-capsule-teal/10 font-semibold"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-menu-admin-link"
                  >
                    <ShieldCheck className="w-4 h-4 text-capsule-teal" />
                    {adminLabel}
                  </ScrollToTopLink>
                )}
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 py-3 px-4 rounded-2xl text-capsule-coral hover:bg-capsule-coral/5"
                >
                  <LogOut className="w-4 h-4" />
                  {HL.logout}
                </button>
              </div>
            ) : (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <ScrollToTopLink
                  to="/login"
                  className="flex items-center gap-3 py-3 px-4 rounded-2xl text-gray-700 hover:bg-capsule-teal/5"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="w-4 h-4 text-capsule-teal" />
                  {HL.login}
                </ScrollToTopLink>
              </div>
            )}
          </div>
        </div>
      )}
      </header>
      {/* Spacer mirrors the live header height so content below (e.g. the
          "Licensed Professionals" banner on product pages) never gets
          clipped by the fixed header on any locale or breakpoint. */}
      <div style={{ height: `${headerH}px` }} aria-hidden="true" />
    </>
  );
};

export default Header;
